import React, { useState } from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Box,
  Divider,
  SwipeableDrawer,
  Theme,
  styled,
  MenuList,
  Menu,
  IconButton,
  ListItemText,
  MenuItem,
  SxProps
} from '@mui/material';
import TwTooltip from '../TwTooltip/TwTooltip';

export interface TwGenericMenuOptions {
  /**
   * Main label for the menu option
   */
  label: string;
  /**
   * Sub-label with different styling
   */
  subLabel?: string;
  /**
   * Menu option action
   */
  action?: () => void;
  /**
   * Extra style classes
   */
  className?: string;
  /**
   * Disabled option true or false
   */
  disabled?: boolean;
  /**
   * Tooltip text
   */
  tooltipText?: string;

  /**
   * Tooltip placement
   * Default: top-end
   */
  toolTipPlacement?:
    | 'bottom-end'
    | 'bottom-start'
    | 'bottom'
    | 'left-end'
    | 'left-start'
    | 'left'
    | 'right-end'
    | 'right-start'
    | 'right'
    | 'top-end'
    | 'top-start'
    | 'top';
}

export interface TwGenericMenuProps {
  /**
   * List of menu options
   */
  options: TwGenericMenuOptions[];
  /**
   * Extra style classes for menu
   */
  className?: string;
  /**
   * Extra style class for menu items
   */
  menuItemClassName?: string;
  /**
   * Visual information of the item the menu options are related to
   */
  menuOwnerSection?: JSX.Element;
  /**
   * sx for menu button
   */
  buttonSx?: SxProps;
}

export const StyledBox = styled(Box)(() => ({
  backgroundColor: '#fff',
  paddingBottom: '22px',
  paddingLeft: '11px',
  paddingRight: '11px',
  height: '100%',
  overflow: 'auto'
}));

export const Puller = styled(Box)(() => ({
  width: '40px',
  height: '4px',
  backgroundColor: '#E5E5E5',
  borderRadius: '4px',
  position: 'absolute',
  top: '20px',
  left: 'calc(50% - 20px)'
}));

const TwGenericMenu: React.FC<TwGenericMenuProps> = ({
  options,
  className,
  menuItemClassName,
  menuOwnerSection,
  buttonSx
}): JSX.Element => {
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState<boolean>(false);

  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLElement>): void => {
    if (matchesMobileOrTablet) {
      setMobileMenuOpen(true);
    } else {
      setAnchorEl(event.currentTarget);
    }

    event.stopPropagation();
  };

  const handleClose = (event: React.MouseEvent<HTMLElement>): void => {
    if (matchesMobileOrTablet) {
      setMobileMenuOpen(false);
    } else {
      setAnchorEl(null);
    }
    event?.stopPropagation();
  };

  const menuItems = options.map((item) => {
    let labelClasses;
    if (item.disabled === true) {
      labelClasses = 'tw-load-menu__disabled';
      item.action = undefined;
    } else {
      labelClasses = 'tw-load-menu__label';
    }

    return (
      <MenuItem
        className={`tid-load-menu__item ${menuItemClassName}`}
        key={item.label}
        onClick={(e): void => {
          if (item.disabled) {
            return;
          }
          item.action && item.action();
          handleClose(e);
          e.stopPropagation();
        }}
      >
        {item.tooltipText ? (
          <TwTooltip title={item.tooltipText} placement={item.toolTipPlacement || 'top-end'}>
            <ListItemText className={`${labelClasses} ${item.className}`}>
              <span>{item.label}</span>
              {item.subLabel && (
                <span style={{ color: '#6B7280', marginLeft: '4px' }}>- {item.subLabel}</span>
              )}
            </ListItemText>
          </TwTooltip>
        ) : (
          <ListItemText className={`${labelClasses} ${item.className}`}>
            <span>{item.label}</span>
            {item.subLabel && (
              <span style={{ color: '#6B7280', marginLeft: '4px' }}>- {item.subLabel}</span>
            )}
          </ListItemText>
        )}
      </MenuItem>
    );
  });

  return (
    <div className={className}>
      <IconButton
        disableRipple
        className="tw-load-menu__menuButton"
        onClick={handleClick}
        sx={{
          ...buttonSx
        }}
      >
        <Box
          style={{
            height: '50px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <MoreVertIcon className="tw-load-menu__menuIcon" sx={{ width: '50px' }} />
        </Box>
      </IconButton>

      {!matchesMobileOrTablet ? (
        <Menu
          open={open || false}
          anchorEl={anchorEl}
          onClose={handleClose}
          className="tw-load-menu__default"
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'left'
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
        >
          <MenuList className="tw-load-menu__list">{menuItems}</MenuList>
        </Menu>
      ) : (
        <SwipeableDrawer
          anchor="bottom"
          open={mobileMenuOpen}
          disableScrollLock
          disableSwipeToOpen
          onClose={handleClose}
          // eslint-disable-next-line @typescript-eslint/no-empty-function
          onOpen={(): void => {}}
          PaperProps={{
            sx: {
              borderTopLeftRadius: '20px',
              borderTopRightRadius: '20px'
            }
          }}
          sx={{
            zIndex: (theme): string => `${Math.max.apply(Math, Object.values(theme.zIndex)) + 1}`
          }}
        >
          <StyledBox
            sx={{
              paddingTop: menuOwnerSection ? '20px' : '56px'
            }}
            onClick={(e): void => {
              e.stopPropagation();
            }}
          >
            <Puller />
            {menuOwnerSection && menuOwnerSection}
            {menuOwnerSection && (
              <Divider sx={{ width: 'calc(100% - 32px)', margin: '0 0 16px 16px' }} />
            )}
            <MenuList className="tw-load-menu__list">{menuItems}</MenuList>
          </StyledBox>
        </SwipeableDrawer>
      )}
    </div>
  );
};

export default TwGenericMenu;
